import avatar from "@/images/avatar.webp"
import classNames from "classnames"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState } from "@/store/globalSlice"
import { allPieces } from "@/utils/data"
import PiecePicker from "@/Components/PiecePicker"
import { useState, useRef, useEffect } from "react"
import { getUserCreds, host, ORIGINAL_GAME_STORE_LINK } from "@/utils/utils"
import { googleSignOut, suppressGoogleAutoSignIn } from "@/utils/googleAuth"
import { DISCORD_INVITE_URL } from "@/Modals/GameEndModalCTA"
import { featureFlags, isDemoMode } from "@mono/constants"
import { MusicSettings } from "@/Components/SoundSettings"
import { useBackgroundMusicPreference } from "@/Hooks/useBackgroundMusicPreference"

interface HeaderProps {}

interface UserData {
  username: string
  email: string
  profileImage?: string
  gamesPlayed: number
  gamesWon: number
  rating: number
}

const isSteamBuild = import.meta.env.VITE_BUILD_TARGET === "steam"

// Every control on the bar sits on the same baseline at the same height, so the
// row reads as one strip rather than a pile of differently sized widgets.
//
// A phone runs the game locked to landscape, so `sm:` (max-height: 499px) is
// the mobile case for this bar. index.css also halves the root font size at
// phone widths, which left every rem-based step here a handful of pixels tall
// - so the mobile steps below are written in raw px, which that scaling cannot
// shrink, and are sized for a thumb rather than a cursor.
const CONTROL_HEIGHT = "h-11 sm:h-[40px]"
const CONTROL_SHELL =
  "rounded-xl border border-white/10 bg-black/30 transition-colors"
const CONTROL_HOVER = "hover:border-white/30 hover:bg-black/50"

// The bar runs flush into the top corners of the screen, which on a phone are
// rounded and, in landscape, may hold the notch - so the first and last control
// were the ones getting clipped. Pad by whatever inset the device reports, with
// a floor that clears the corner radius on the phones that report none at all.
//
// The bar itself deliberately stays full-bleed - it is a dark band, so a shaved
// corner or a cutout sitting in it is invisible, and the pages below it only
// have to hold their own content clear (see utils/safeArea.ts). The unprefixed
// pair replaces `px-6` and covers a portrait phone, where `sm:` does not match;
// the `sm:` pair keeps the wider thumb gutter it already had on a landscape one.
const SAFE_X_PADDING =
  "pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] " +
  "sm:pl-[max(18px,var(--safe-left))] sm:pr-[max(18px,var(--safe-right))]"

// A top inset is 0 on a landscape phone - the cutout has moved to a side edge
// and the system bars are hidden - so this costs nothing there. It is the
// portrait case, and any device that does report one, that would otherwise run
// the controls under the status bar. The bar grows by the inset rather than
// giving up any of its own height, which on a short screen there is none of.
//
// Raw env() rather than --safe-top: that variable carries an 8px floor for
// rounded corners (App.css), and paying it here would cost every phone 8px of
// the height the lobby is already short of, to clear a corner that shaves
// nothing off a full-bleed dark band.
const SAFE_TOP_PADDING =
  "h-[calc(5rem+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] " +
  "sm:h-[calc(52px+env(safe-area-inset-top,0px))]"

// Shared shell for the panels that drop out of the right-hand controls.
const MENU_PANEL =
  "absolute right-0 top-full z-[100] mt-3 rounded-2xl sm:mt-[10px] border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200"

const Divider = () => (
  <span className="h-7 w-px shrink-0 bg-white/10 sm:h-[22px]" aria-hidden="true" />
)

// The arrow that pins a dropdown to the control it came out of. Lives outside
// the panel's own box, so no panel may clip its overflow.
const MenuArrow = () => (
  <div
    className="absolute -top-2 right-5 h-4 w-4 rotate-45 border-l border-t border-white/10 bg-gray-900 sm:-top-[7px] sm:right-[16px] sm:h-[14px] sm:w-[14px]"
    aria-hidden="true"
  />
)

// A small dark bubble that appears above whatever it's wrapped around on
// hover — used in place of the native `title` attribute so it matches the
// rest of the app's styling instead of the browser's default tooltip.
const HoverLabel: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <span className="group/hoverlabel relative flex items-center">
    {children}
    <span className="pointer-events-none absolute bottom-full left-1/2 z-[110] mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-gray-950 px-2.5 py-1 font-poppins text-xs text-white opacity-0 sm:text-[11px] shadow-xl transition-opacity duration-150 group-hover/hoverlabel:opacity-100">
      {label}
    </span>
  </span>
)

const Navbar: React.FC<HeaderProps> = () => {
  const dispatch = useAppDispatch()
  const playerCounts = useAppSelector((state) => state.global.playerCounts)
  const account = useAppSelector((state) => state.global.userData)
  const preferredPieceId = useAppSelector(
    (state) => state.global.preferredPieceId
  )
  const [showDropdown, setShowDropdown] = useState(false)
  const [showPieceMenu, setShowPieceMenu] = useState(false)
  const [showMusicMenu, setShowMusicMenu] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const pieceMenuRef = useRef<HTMLDivElement>(null)
  const musicMenuRef = useRef<HTMLDivElement>(null)
  const nav = useNavigate()
  const location = useLocation()
  const { enabled: musicEnabled, toggle: toggleMusic } =
    useBackgroundMusicPreference()

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userCreds = getUserCreds()
        if (!userCreds.email) {
          setIsLoading(false)
          return
        }

        const response = await fetch(`${host}/api/user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: userCreds.email }),
        })

        if (response.ok) {
          const data = await response.json()
          setUserData(data)
        } else {
          console.error("Failed to fetch user data")
        }
      } catch (error) {
        console.error("Error fetching user data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserData()
  }, [])

  // Keep the name and picture in step with the account in the store, so saving
  // on the settings page updates the header on the spot instead of waiting for
  // the next reload. The rest (rating, games) still comes from the fetch above.
  useEffect(() => {
    if (!account?.email) return
    setUserData((current) =>
      current
        ? {
            ...current,
            username: account.username ?? current.username,
            profileImage: account.profileImage ?? current.profileImage,
          }
        : current
    )
  }, [account?.username, account?.profileImage])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
      if (
        pieceMenuRef.current &&
        !pieceMenuRef.current.contains(event.target as Node)
      ) {
        setShowPieceMenu(false)
      }
      if (
        musicMenuRef.current &&
        !musicMenuRef.current.contains(event.target as Node)
      ) {
        setShowMusicMenu(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    // Without this the login screen's silent Google attempt fires the instant we
    // land on it and signs them straight back into the account they just left.
    suppressGoogleAutoSignIn()
    void googleSignOut()
    dispatch(setState({ isLoggedIn: false }))
    nav("/login")
  }

  const menuItems = [
    {
      label: "Update profile",
      hint: "Name, picture and piece",
      icon: "fa-solid fa-user-pen",
      danger: false,
      onClick: () => nav("/profile-settings"),
    },
    // The Steam build signs you in silently from the Steam session, so there is
    // no other account to switch to — logging out would just bounce you through
    // the connecting screen and back in.
    ...(isSteamBuild
      ? []
      : [
          {
            label: "Log out",
            hint: "End this session",
            icon: "fa-solid fa-right-from-bracket",
            danger: true,
            onClick: handleLogout,
          },
        ]),
  ]

  // Lobby is the one people reach for every session, so it gets the middle of
  // the bar and the only filled button on it; the other two flank it quietly.
  const sideLinks = [
    { path: "/stats", label: "Stats", icon: "fa-solid fa-chart-simple" },
    { path: "/profile-settings", label: "Settings", icon: "fa-solid fa-gear" },
  ]

  // Get profile image source. The bar's own fetch above is really only there
  // for the rating and games figures; the picture is also on the account in the
  // store, so fall back to that rather than to the placeholder — otherwise a
  // fetch that failed, or one still in flight when the picture was saved, left
  // the bar showing the default avatar with nothing to ever replace it.
  const getProfileImageSrc = () => {
    return userData?.profileImage || account?.profileImage || avatar
  }

  // The piece this player has claimed for every game, or undefined while they
  // are still taking whatever the game deals them.
  const preferredPieceIndex = allPieces.findIndex(
    (piece) => piece.id === preferredPieceId
  )
  const preferredPiece = allPieces[preferredPieceIndex]

  const renderSideLink = (link: (typeof sideLinks)[number]) => {
    const isActive = location.pathname === link.path
    return (
      <Link
        key={link.path}
        to={link.path}
        title={link.label}
        className={classNames(
          CONTROL_HEIGHT,
          "flex items-center gap-2 rounded-xl border px-3.5 font-poppins text-sm font-semibold uppercase tracking-[0.08em] transition-colors sm:gap-[6px] sm:px-[12px] sm:text-[12px]",
          isActive
            ? "border-amber-400/50 bg-amber-400/20 text-amber-100"
            : "border-white/10 bg-black/30 text-white/60 hover:border-white/30 hover:bg-black/50 hover:text-white"
        )}
      >
        <i className={classNames(link.icon, "text-base sm:text-[17px]")}></i>
        <span className="hidden xl:inline">{link.label}</span>
      </Link>
    )
  }

  return (
    // `backdrop-blur` makes this a stacking context of its own, so a z-index on
    // the dropdowns inside only orders them against each other — without the
    // `relative z-40` here the page below (painted later) covers them.
    //
    // Three grid columns rather than flex: the outer two share the leftover
    // space evenly, which parks the middle column dead centre. When the right
    // side outgrows its share it takes the room it needs and the centre drifts
    // instead of anything overlapping.
    <header
      className={classNames(
        "relative z-40 grid w-full shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-b border-white/10 bg-black/50 backdrop-blur-md sm:gap-[8px]",
        SAFE_TOP_PADDING,
        SAFE_X_PADDING
      )}
    >
      {/* Left: where to find us. */}
      <div className="flex items-center gap-2 justify-self-start sm:gap-[8px]">
        {isDemoMode && (
          <a
            href={ORIGINAL_GAME_STORE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            title="Wishlist on Steam"
            className={classNames(
              CONTROL_HEIGHT,
              "inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#1b2838] px-3.5 font-poppins text-sm font-semibold text-white transition-colors hover:bg-[#2a475e] sm:gap-[6px] sm:px-[12px] sm:text-[12px]"
            )}
          >
            <i className="fa-brands fa-steam text-base sm:text-[17px]" />
            <span className="hidden xl:inline">Wishlist</span>
          </a>
        )}

        <a
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Join our Discord"
          className={classNames(
            CONTROL_HEIGHT,
            "inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#5865F2] px-3.5 font-poppins text-sm font-semibold text-white transition-colors hover:bg-[#4752C4] sm:gap-[6px] sm:px-[12px] sm:text-[12px]"
          )}
        >
          <i className="fa-brands fa-discord text-base sm:text-[17px]" />
          <span className="hidden xl:inline">Discord</span>
        </a>
      </div>

      {/* Centre: where you can go, with the lobby front and centre. */}
      {featureFlags.navbarLinks ? (
        <nav className="flex items-center gap-2 justify-self-center sm:gap-[8px]">
          {renderSideLink(sideLinks[0])}

          <Link
            to="/lobby"
            title="Go to the lobby"
            className={classNames(
              CONTROL_HEIGHT,
              // Same tactile "key" as the in-game controls: a hard bottom lip
              // that collapses under the press.
              "flex items-center gap-2.5 rounded-xl border border-amber-300/40 bg-gradient-to-b from-amber-400 to-amber-600 px-6 font-poppins text-sm font-bold uppercase tracking-[0.12em] text-black transition-[transform,box-shadow] duration-150 sm:gap-[8px] sm:px-[18px] sm:text-[13px]",
              "shadow-[0_4px_0_0_#92400e,0_12px_26px_-8px_rgba(251,191,36,0.55)]",
              "hover:-translate-y-[2px] hover:shadow-[0_4px_0_0_#92400e,0_16px_34px_-8px_rgba(251,191,36,0.7)]",
              "active:translate-y-[4px] active:shadow-[0_0_0_0_#92400e,0_4px_12px_-4px_rgba(0,0,0,0.5)]",
              location.pathname === "/lobby" && "ring-2 ring-amber-200/70"
            )}
          >
            <i className="fa-solid fa-dice text-base sm:text-[16px]"></i>
            <span>Lobby</span>
          </Link>

          {renderSideLink(sideLinks[1])}
        </nav>
      ) : (
        <div />
      )}

      {/* Right: who you are, and how the game sounds and looks. */}
      <div className="flex items-center gap-3 justify-self-end sm:gap-[8px]">
        <div
          className={classNames(
            CONTROL_HEIGHT,
            CONTROL_SHELL,
            "flex items-center gap-3 px-3 font-poppins text-sm sm:gap-[10px] sm:px-[10px] sm:text-[13px]"
          )}
        >
          <HoverLabel label={`${playerCounts.online} players online`}>
            <span className="flex items-center gap-1.5 sm:gap-[6px]">
              <span className="relative flex h-2 w-2 sm:h-[8px] sm:w-[8px]">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500 sm:h-[8px] sm:w-[8px]"></span>
              </span>
              <span className="font-semibold tabular-nums text-white">
                {playerCounts.online}
              </span>
            </span>
          </HoverLabel>
          <span className="h-4 w-px bg-white/10 sm:h-[16px]" aria-hidden="true" />
          <HoverLabel label={`${playerCounts.inGame} players in a game`}>
            <span className="flex items-center gap-1.5 sm:gap-[6px]">
              <span className="h-2 w-2 rounded-full bg-amber-400 sm:h-[8px] sm:w-[8px]"></span>
              <span className="font-semibold tabular-nums text-white">
                {playerCounts.inGame}
              </span>
            </span>
          </HoverLabel>
        </div>

        <Divider />

        {/* The piece you play as, straight on the header so you know what
            you'll be before a game starts. Unset, it's an invitation to pick
            one; set, it's a shortcut back to the same picker. */}
        <div className="relative" ref={pieceMenuRef}>
          <button
            onClick={() => setShowPieceMenu((open) => !open)}
            data-testid="navbar.piece-button"
            title={
              preferredPiece
                ? `Playing as piece ${
                    preferredPieceIndex + 1
                  } — click to change`
                : "Choose the piece you play as"
            }
            className={classNames(
              CONTROL_HEIGHT,
              "flex items-center justify-center gap-2 rounded-xl border px-2.5 font-poppins text-sm font-semibold transition-colors sm:gap-[6px] sm:px-[10px] sm:text-[13px]",
              preferredPiece
                ? `border-white/10 bg-black/30 text-white ${CONTROL_HOVER}`
                : "border-amber-400/50 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20"
            )}
          >
            {preferredPiece ? (
              <img
                src={preferredPiece.imgSrc}
                alt={`Piece ${preferredPieceIndex + 1}`}
                data-testid="navbar.piece-image"
                className="h-7 w-7 translate-y-[2px] object-contain sm:h-[26px] sm:w-[26px]"
              />
            ) : (
              <>
                <i className="fa-solid fa-chess-pawn text-base sm:text-[17px]"></i>
                <span className="hidden xl:inline">Choose piece</span>
              </>
            )}
          </button>

          {showPieceMenu && (
            <div
              data-testid="navbar.piece-menu"
              className={classNames(
                MENU_PANEL,
                "w-[24rem] p-4 sm:w-[300px] sm:p-[12px]"
              )}
            >
              <MenuArrow />
              {/* A landscape phone cannot show the whole grid at once, so it
                  scrolls inside - and, as with the music panel, the arrow above
                  has to stay outside the scroller or it gets clipped away. */}
              <div className="sm:max-h-[60vh] sm:overflow-y-auto">
                <PiecePicker
                  className="!grid-cols-3"
                  testIdPrefix="navbar"
                  onSelect={() => setShowPieceMenu(false)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Music: the note mutes on the spot, the caret opens the same volume
            and track controls the settings screen has. */}
        <div
          className={classNames(
            CONTROL_HEIGHT,
            CONTROL_SHELL,
            "relative flex items-stretch"
          )}
          ref={musicMenuRef}
        >
          <button
            onClick={toggleMusic}
            data-testid="navbar.background-music-toggle"
            aria-label={musicEnabled ? "Turn music off" : "Turn music on"}
            title={musicEnabled ? "Turn music off" : "Turn music on"}
            className={classNames(
              "flex w-10 items-center justify-center rounded-l-xl transition-colors hover:bg-white/5 sm:w-[42px]",
              musicEnabled ? "text-amber-300" : "text-white/40"
            )}
          >
            <span className="relative flex items-center justify-center">
              <i className="fa-solid fa-music sm:text-[16px]"></i>
              {!musicEnabled && (
                <span
                  className="pointer-events-none absolute left-1/2 top-1/2 h-[2px] w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current sm:w-[22px]"
                  aria-hidden="true"
                />
              )}
            </span>
          </button>
          <button
            onClick={() => setShowMusicMenu((open) => !open)}
            aria-label="Music settings"
            title="Music settings"
            className="flex w-7 items-center justify-center rounded-r-xl border-l border-white/10 text-white/50 transition-colors hover:bg-white/5 hover:text-white sm:w-[28px]"
          >
            <i
              className={classNames(
                "fa-solid fa-chevron-down text-[10px] transition-transform sm:text-[11px]",
                showMusicMenu && "rotate-180"
              )}
            ></i>
          </button>

          {showMusicMenu && (
            <div
              className={classNames(
                MENU_PANEL,
                "w-[23rem] p-4 sm:w-[300px] sm:p-[12px]"
              )}
            >
              <MenuArrow />
              <h2 className="mb-3 font-fourth text-base font-bold tracking-wide text-white sm:text-[15px]">
                Music
              </h2>
              {/* The panel scrolls inside rather than growing past a short
                  screen — the arrow above has to stay outside the scroller or
                  it gets clipped away. */}
              <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto">
                <MusicSettings testIdPrefix="navbar.music" />
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Account */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown((open) => !open)}
            title="Your account"
            className={classNames(
              CONTROL_HEIGHT,
              CONTROL_SHELL,
              CONTROL_HOVER,
              "flex items-center gap-2.5 py-1 pl-1 pr-2.5 sm:gap-[6px] sm:py-[3px] sm:pl-[4px] sm:pr-[10px]"
            )}
          >
            <img
              src={getProfileImageSrc()}
              className="h-9 w-9 rounded-full object-cover ring-1 ring-white/20 sm:h-[32px] sm:w-[32px]"
              alt={userData?.username || "User"}
            />
            {!isLoading && userData && (
              <span className="hidden max-w-[9rem] truncate font-poppins text-sm font-semibold text-white 2xl:block">
                {userData.username}
              </span>
            )}
            <i
              className={classNames(
                "fa-solid fa-chevron-down text-[10px] text-white/40 transition-transform sm:text-[11px]",
                showDropdown && "rotate-180"
              )}
            ></i>
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className={classNames(MENU_PANEL, "w-[19rem] sm:w-[280px]")}>
              <MenuArrow />

              {/* Who you are, at the size the rest of the app shows you. */}
              <div className="flex items-center gap-3 rounded-t-2xl border-b border-white/10 bg-gradient-to-br from-amber-500/20 to-transparent px-4 py-4 sm:gap-[10px] sm:px-[12px] sm:py-[10px]">
                <img
                  src={getProfileImageSrc()}
                  className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-amber-400/60 sm:h-[40px] sm:w-[40px]"
                  alt={userData?.username || "User"}
                />
                <div className="min-w-0">
                  <div className="truncate font-poppins text-sm font-bold text-white sm:text-[13px]">
                    {userData?.username || "Player"}
                  </div>
                  <div className="truncate font-poppins text-xs text-white/50 sm:text-[11px]">
                    {userData?.email || "Not signed in"}
                  </div>
                </div>
              </div>

              {/* The three numbers worth a glance before you head back in. */}
              {userData && (
                <div className="grid grid-cols-3 border-b border-white/10">
                  {[
                    { label: "Rating", value: userData.rating, accent: true },
                    { label: "Played", value: userData.gamesPlayed },
                    { label: "Won", value: userData.gamesWon },
                  ].map((stat, index) => (
                    <div
                      key={stat.label}
                      className={classNames(
                        "px-2 py-3 text-center sm:px-[6px] sm:py-[8px]",
                        index > 0 && "border-l border-white/10"
                      )}
                    >
                      <div
                        className={classNames(
                          "font-poppins text-lg font-bold tabular-nums leading-none sm:text-[17px]",
                          stat.accent ? "text-amber-300" : "text-white"
                        )}
                      >
                        {stat.value ?? 0}
                      </div>
                      <div className="mt-1 font-poppins text-[10px] uppercase tracking-wider text-white/40 sm:mt-[4px]">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-1 p-2 sm:gap-[4px] sm:p-[8px]">
                {menuItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      setShowDropdown(false)
                      item.onClick()
                    }}
                    className={classNames(
                      "flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors sm:gap-[10px] sm:px-[8px] sm:py-[8px]",
                      item.danger
                        ? "text-red-400 hover:bg-red-500/10"
                        : "text-white/80 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <span
                      className={classNames(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border sm:h-[34px] sm:w-[34px] sm:text-[15px]",
                        item.danger
                          ? "border-red-500/20 bg-red-500/10"
                          : "border-white/10 bg-white/5"
                      )}
                    >
                      <i className={item.icon}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-poppins text-sm font-semibold sm:text-[13px]">
                        {item.label}
                      </span>
                      <span className="block truncate font-poppins text-[11px] text-white/40">
                        {item.hint}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Navbar
